import { SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Input } from 'antd';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import { fetchInsFamousPostLikesData } from '@/lib/service/anonymously';
import { InstagramPostsLikesItemModel, InstagramQueryPostItemModel } from '@/types/anonymously.interface';
import { cn } from '@/lib/utils';
import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import PostLikesSkeleton from './PostLikesSkeleton';

const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), { ssr: false });

interface PostLikesInfoProps {
  item: InstagramQueryPostItemModel;
  /** 是否显示移动端预览 */
  isMobilePreview?: boolean;
  /** 控制显示的数量上限 */
  maxDisplayCount?: number | false;
  /** 自定义加载按钮点击事件，将取代默认加载事件 */
  onLoadMoreClick?: () => void;
}

// 自定义hook: 处理点赞数据获取和状态管理
const useLikesData = (postCode: string | undefined, maxDisplayCount?: number | false) => {
  const pageSize = 20;
  const { t } = useTranslation();

  // 点赞数据相关状态
  const [likesData, setLikesData] = useState<InstagramPostsLikesItemModel[]>([]);
  const [displayLikes, setDisplayLikes] = useState<InstagramPostsLikesItemModel[]>([]);
  const [totalLikes, setTotalLikes] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // 加载状态
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // 添加请求状态引用
  const requestStateRef = useRef<{ postCode: string | null; isFetching: boolean }>({
    postCode: null,
    isFetching: false,
  });

  // 重置所有状态
  const resetState = useCallback(() => {
    setLikesData([]);
    setDisplayLikes([]);
    setCurrentPage(1);
    setSearchKeyword('');
    setError('');
    setTotalLikes(0);
    setHasMore(true);
  }, []);

  // 获取点赞数据
  const fetchLikes = useCallback(
    async (code: string) => {
      if (!code) return;

      // 防止重复请求
      if (requestStateRef.current.isFetching && requestStateRef.current.postCode === code) {
        return;
      }

      requestStateRef.current = {
        postCode: code,
        isFetching: true,
      };

      setInitialLoading(true);
      setError('');

      try {
        const res = await fetchInsFamousPostLikesData(code, { isPremium: false, bizFuncName: 'post_likes' });
        if (res?.code === 0 && res?.data) {
          const { like_users = [], user_count = 0 } = res.data;

          setLikesData(like_users);
          setTotalLikes(user_count);

          // 初始显示第一页数据
          setDisplayLikes(like_users.slice(0, maxDisplayCount || pageSize));
          setHasMore(like_users.length > (maxDisplayCount || pageSize));
          setCurrentPage(maxDisplayCount === false ? 1 : 0);
        } else {
          setError(res?.message || t('Failed to load likes'));
          console.error('Failed to fetch likes:', res?.message);
        }
      } catch (error) {
        setError(t('Failed to load likes'));
        console.error('Error fetching likes:', error);
      } finally {
        setInitialLoading(false);
        requestStateRef.current.isFetching = false;
      }
    },
    [maxDisplayCount, pageSize, t]
  );

  // 过滤后的点赞数据
  const filteredLikesData = useMemo(() => {
    if (!searchKeyword.trim()) return likesData;

    const keyword = searchKeyword.trim().toLowerCase();
    return likesData.filter((like) => {
      const username = like.username?.toLowerCase() || '';
      const fullName = like.full_name?.toLowerCase() || '';

      return username.includes(keyword) || fullName.includes(keyword);
    });
  }, [likesData, searchKeyword]);

  // 更新搜索关键词，同时更新显示的数据
  const updateSearchKeyword = useCallback(
    (value: string) => {
      setSearchKeyword(value);
      setCurrentPage(1);

      // 重置分页状态
      if (value.trim() === '') {
        setDisplayLikes(likesData.slice(0, pageSize));
        setHasMore(likesData.length > pageSize);
      } else {
        const filtered = likesData.filter((like) => {
          const keyword = value.trim().toLowerCase();
          const username = like.username?.toLowerCase() || '';
          const fullName = like.full_name?.toLowerCase() || '';
          return username.includes(keyword) || fullName.includes(keyword);
        });

        setDisplayLikes(filtered.slice(0, pageSize));
        setHasMore(filtered.length > pageSize);
      }
    },
    [likesData]
  );

  // 加载更多数据
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;

    setLoading(true);

    try {
      const nextPage = currentPage + 1;
      const dataSource = searchKeyword.trim() ? filteredLikesData : likesData;
      const nextPageData = dataSource.slice(0, nextPage * pageSize);

      setDisplayLikes(nextPageData);
      setCurrentPage(nextPage);
      setHasMore(nextPageData.length < dataSource.length);
    } catch (error) {
      console.error('Error loading more likes:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filteredLikesData, hasMore, likesData, loading, pageSize, searchKeyword]);

  // 清除搜索关键词
  const clearSearch = useCallback(() => {
    updateSearchKeyword('');
  }, [updateSearchKeyword]);

  // 当postCode变化时，重置状态并获取新数据
  useEffect(() => {
    if (postCode) {
      // 只有当postCode不同或之前没有请求过时才重置状态并获取新数据
      if (requestStateRef.current.postCode !== postCode) {
        resetState();
        fetchLikes(postCode);
      }
    }
  }, [postCode, resetState, fetchLikes]);

  return {
    likesData,
    displayLikes,
    totalLikes,
    hasMore,
    searchKeyword,
    initialLoading,
    loading,
    error,
    filteredLikesData,
    updateSearchKeyword,
    loadMore,
    clearSearch,
    fetchLikes,
    resetState,
  };
};

const PostLikes = ({ item, isMobilePreview, maxDisplayCount = false, onLoadMoreClick }: PostLikesInfoProps) => {
  const { t } = useTranslation();
  const { guideToRegisterOrLogin } = useRegisterOrLoginGuide();

  // 使用自定义hook管理点赞数据
  const {
    likesData,
    displayLikes,
    totalLikes,
    hasMore,
    searchKeyword,
    initialLoading,
    loading,
    error,
    updateSearchKeyword,
    loadMore,
    clearSearch,
    fetchLikes,
    resetState,
  } = useLikesData(item?.code, maxDisplayCount);

  // 搜索框变化处理
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSearchKeyword(e.target.value);
  };

  // 加载更多
  const handleLoadMore = () => {
    guideToRegisterOrLogin(() => {
      if (onLoadMoreClick) {
        onLoadMoreClick();
      } else {
        loadMore();
      }
    });
  };

  // 重试加载
  const handleRetry = () => {
    if (!item?.code) return;
    resetState();
    fetchLikes(item.code);
  };

  // 在新标签页打开当前页面，携带 media_name 参数
  const handleOpenInNewTab = (mediaName: string) => {
    guideToRegisterOrLogin(() => {
      window.open(`/instagram-viewer/post-likes?media_name=${mediaName}`, '_blank');
    });
  };

  // 渲染点赞列表内容
  const renderLikesListContent = () => {
    // 显示错误
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 p-4">
          <p className="text-red-500 mb-2">{error}</p>
          <Button onClick={handleRetry}>{t('Retry')}</Button>
        </div>
      );
    }

    // 显示初始加载状态 - 使用骨架屏保持布局稳定
    if (initialLoading) {
      return <PostLikesSkeleton isMobilePreview={isMobilePreview} />;
    }

    // 显示无数据状态
    if (displayLikes.length === 0 && !loading) {
      return (
        <div className="flex flex-col items-center justify-center h-60 text-gray-500 p-6">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchKeyword ? t('No matching likes found') : t('No likes yet')}
          />
          {searchKeyword && (
            <Button type="link" onClick={clearSearch} className="mt-2">
              {t('Clear search')}
            </Button>
          )}
        </div>
      );
    }

    // 渲染单个点赞项
    const renderLikeItem = (like: InstagramPostsLikesItemModel, index: number) => (
      <div key={index} className="p-4 hover:bg-gray-50 transition-colors max-md:p-2.5">
        <div className="flex items-center gap-3">
          {/* 序号 */}
          <div className="flex-shrink-0 w-5 h-5 text-sm text-center rounded-full bg-[#EAEDF0]">{index + 1}</div>

          {/* 用户头像 */}
          <div
            className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 cursor-pointer"
            onClick={() => handleOpenInNewTab(like.username || '')}
          >
            <LocalImage src={like.profile_pic_url} width={40} height={40} alt={like.full_name || ''} />
          </div>

          <div className="flex-1 min-w-0">
            {/* 用户信息 */}
            <div className="flex flex-wrap items-center">
              <span
                className="font-medium text-gray-900 mr-1 cursor-pointer"
                onClick={() => handleOpenInNewTab(like.username || '')}
              >
                {like.full_name || ''}
              </span>

              <a
                className="flex items-center text-gray-500 text-sm cursor-pointer"
                href={`https://www.instagram.com/${like.username}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {/* Instagram图标 */}
                <Image src="/images/follow/ins-mark.webp" width={16} height={16} alt="Instagram" className="mx-1" />

                <span>@{like.username}</span>

                {/* 蓝V标识 */}
                {like.is_verified && (
                  <Image src="/images/follow/blue-mark.webp" width={16} height={16} alt="Verified" className="ml-1" />
                )}
              </a>
            </div>
          </div>

          {/* 私密账号标识 */}
          {like.is_private && (
            <div className="flex-shrink-0 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-500">{t('Private')}</div>
          )}
        </div>
      </div>
    );

    // 显示点赞列表
    return (
      <>
        <div className="">{displayLikes.map(renderLikeItem)}</div>

        {/* 加载更多按钮和加载状态 */}
        {hasMore && displayLikes.length > 0 && (
          <div className="p-4 text-center max-md:px-0 max-md:py-2.5">
            <Button
              onClick={handleLoadMore}
              loading={loading}
              className="px-6 font-medium max-md:w-full max-md:h-[42px] max-md:p-0 max-md:text-sm"
            >
              {loading ? t('Loading...') : t('Load More')}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={cn(
        'w-full flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm h-full',
        'max-md:border-none max-md:shadow-none'
      )}
    >
      {/* 标题和搜索框 - 固定部分 */}
      <div className={cn('p-4 bg-white border-gray-200 max-md:p-0 max-md:pb-2', {})}>
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-medium max-md:text-sm">
            {totalLikes} {t('Likes')}
          </h3>
          {!isMobilePreview && (
            <>
              {likesData.length > 0 && (
                <p className="text-sm text-gray-500 flex items-center">
                  <span className="text-red-500 mr-1">❤</span>
                  {t('Last')} {likesData.length} {t('likes (most recent order)')}
                </p>
              )}
              <div className="relative mt-2">
                <Input
                  placeholder={t('Search')}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={searchKeyword}
                  onChange={handleSearchChange}
                  className="w-full h-9 rounded-lg max-md:h-10"
                  allowClear
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* 点赞列表 - 可滚动部分 */}
      <div className="flex-1 overflow-y-auto bg-white" style={{ maxHeight: 'calc(100% - 150px)' }}>
        {renderLikesListContent()}
      </div>
    </div>
  );
};

// 使用 memo 包装组件避免不必要的重渲染
export default memo(PostLikes, (prevProps, nextProps) => {
  // 只有当 item.code 或 isMobilePreview 改变时才重新渲染
  return (
    prevProps.item?.code === nextProps.item?.code &&
    prevProps.isMobilePreview === nextProps.isMobilePreview &&
    prevProps.maxDisplayCount === nextProps.maxDisplayCount
  );
});
